// Ayuda con la cuarentena y la ingesta: lotes fallidos entre cuentas, motivos que se repiten por conector, buzones
// de facturas y agentes conectados. Baja las intervenciones, que es lo que encarece servir una cuenta (§14).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Cargando, PageHead, Tabla, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { desde, momento } from '../lib/formato';
import { useSesion } from '../lib/sesion';

export function Ingesta() {
  const [dias, setDias] = useState(14);
  const q = useQuery({ queryKey: ['panel-ingesta', dias], queryFn: () => api<any>(`/api/panel/ingesta?dias=${dias}`) });
  if (q.isLoading || !q.data) return <Cargando />;

  return (
    <div className="page">
      <PageHead title="Ingesta y conectores" subtitle="Lo que dejó de entrar y por qué, entre todas las cuentas. Cada motivo que se repite es una intervención que se puede evitar." />
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[7, 14, 30].map((d) => <button key={d} className={`chip ${d === dias ? 'tabla-row-selected' : ''}`} onClick={() => setDias(d)}>{d} días</button>)}
      </div>

      <div className="eyebrow">Lo que se repite: mismo motivo, varias veces</div>
      {!q.data.errores.length ? <Vacio titulo="Sin errores repetidos">Nada quedó esperando por el mismo motivo en este periodo.</Vacio> : (
        <Tabla rowKey="k" rows={q.data.errores.map((e: any, i: number) => ({ ...e, k: i }))}
          columns={[
            { key: 'negocio', label: 'Negocio' },
            { key: 'fuente', label: 'Dónde', render: (e: any) => (e.fuente === 'cuarentena' ? 'Cuarentena' : 'Captura') },
            { key: 'via', label: 'Por dónde' },
            { key: 'motivo', label: 'Motivo' },
            { key: 'n', label: 'Veces', numeric: true, render: (e: any) => <Badge state={e.n > 5 ? 'error' : 'pending'}>{e.n}</Badge> },
            { key: 'ultimo', label: 'Último', render: (e: any) => momento(e.ultimo) },
          ]} />
      )}

      <div className="eyebrow" style={{ marginTop: 18 }}>Lotes que se fueron a cuarentena o se revirtieron</div>
      {!q.data.lotes.length ? <Vacio titulo="Ningún lote con problemas">Todo lo que entró quedó completo.</Vacio> : (
        <Tabla rowKey="id" rows={q.data.lotes}
          columns={[
            { key: 'negocio', label: 'Negocio' },
            { key: 'numero', label: 'Lote', numeric: true },
            { key: 'via', label: 'Por dónde' },
            { key: 'origen', label: 'Origen' },
            { key: 'registros', label: 'Registros', numeric: true },
            { key: 'en_cuarentena', label: 'En cuarentena', numeric: true },
            { key: 'estado', label: 'Estado', render: (l: any) => <Badge state={l.estado === 'revertido' ? 'error' : 'pending'}>{l.estado === 'revertido' ? 'Revertido' : 'Activo'}</Badge> },
            { key: 'creado_en', label: 'Cuándo', render: (l: any) => momento(l.creado_en) },
          ]} />
      )}

      <div className="eyebrow" style={{ marginTop: 18 }}>Buzones de facturas</div>
      <Tabla rowKey="cuentaId" rows={q.data.buzones}
        columns={[
          { key: 'negocio', label: 'Negocio' },
          { key: 'buzon', label: 'Buzón', render: (b: any) => <code style={{ fontSize: 12 }}>{b.buzon}@facturas.glubi.co</code> },
          { key: 'capturasConError', label: 'Facturas con error', numeric: true, render: (b: any) => (b.capturasConError ? <Badge state="error">{b.capturasConError}</Badge> : <span className="muted">—</span>) },
        ]} />

      <div className="eyebrow" style={{ marginTop: 18 }}>Conectores por cuenta</div>
      <Tabla rowKey="k" rows={q.data.conectores.map((c: any, i: number) => ({ ...c, k: i }))}
        columns={[
          { key: 'negocio', label: 'Negocio' },
          { key: 'solucion', label: 'Solución' },
          { key: 'tipo', label: 'Tipo' },
          { key: 'entidad', label: 'Dato' },
          { key: 'detalle', label: 'Qué espera' },
        ]} />

      <div className="eyebrow" style={{ marginTop: 18 }}>Agentes conectados</div>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>Nombre, alcance y último uso. El valor de un token no se ve desde acá: no sale de la cuenta ni para el equipo.</p>
      <Tabla rowKey="k" rows={q.data.tokens.map((t: any, i: number) => ({ ...t, k: i }))}
        columns={[
          { key: 'negocio', label: 'Negocio' },
          { key: 'nombre', label: 'Token' },
          { key: 'agente', label: 'Agente' },
          { key: 'alcance', label: 'Dirección' },
          { key: 'estado', label: 'Estado', render: (t: any) => <Badge state={t.estado === 'activo' ? 'ok' : 'pending'}>{t.estado}</Badge> },
          { key: 'ultimo_uso_en', label: 'Último uso', render: (t: any) => (t.ultimo_uso_en ? desde(t.ultimo_uso_en) : 'Nunca') },
        ]} />
    </div>
  );
}
